import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { FloatingThemeToggle } from "./components/FloatingThemeToggle";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { AdminPage } from "./pages/AdminPage";
import { SubscriptionPage } from "./pages/SubscriptionPage";
import { PendingApprovalPage } from "./pages/PendingApprovalPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";

const FullPageLoader = () => (
  <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-[#065F46]" />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (user) return <Navigate to="/" replace />;
  return children;
};

// Smart router: redirects user to the right surface based on access state
const SmartHome = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;

  // Admin always goes to admin dashboard
  if (user.is_admin) return <AdminPage />;

  // Pending or suspended → dedicated screen
  if (user.status === 'PENDING_APPROVAL' || user.status === 'SUSPENDED') {
    return <PendingApprovalPage />;
  }

  // No access (trial expired / no subscription) → subscription page
  if (!user.has_access) {
    if (location.pathname.startsWith('/subscription')) {
      return <SubscriptionPage />;
    }
    return <Navigate to="/subscription" replace />;
  }

  // Has access — render full dashboard
  return <DashboardPage />;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/success"
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <SmartHome />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <FloatingThemeToggle />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: 'var(--toast-bg, #FFFFFF)',
                border: '1px solid var(--toast-border, #E2E8F0)',
                color: 'var(--toast-fg, #0F172A)',
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
